import type { CustomerConfig } from '@/entities/customer-config';
import { FeatureFlag } from '@/types/feature-flag';

export type DexVersion = 'v2' | 'v1';

export type ActiveDexInfo = {
  version: DexVersion;
  featureFlag: keyof CustomerConfig['featureFlags'];
};

/**
 * Determines the active dex version based on feature flags.
 * This is the SINGLE SOURCE OF TRUTH for dex version resolution.
 *
 * Priority: dexV2 > dex (v1)
 *
 * Returns null if no dex feature is enabled.
 */
export function getActiveDexVersion(
  featureFlags: CustomerConfig['featureFlags'],
  isInternal: boolean,
): ActiveDexInfo | null {
  const isEnabled = (flag: FeatureFlag) =>
    flag === FeatureFlag.PUBLIC ||
    (flag === FeatureFlag.INTERNAL && isInternal);

  if (isEnabled(featureFlags.newDataExplorer)) {
    return { version: 'v2', featureFlag: 'newDataExplorer' };
  }

  if (isEnabled(featureFlags.dataExplorer)) {
    return { version: 'v1', featureFlag: 'dataExplorer' };
  }

  return null;
}

/**
 * Checks if ANY dex feature is enabled (regardless of version).
 * Useful for determining sidebar visibility without caring about version.
 */
export function isDexEnabled(
  featureFlags: CustomerConfig['featureFlags'],
  isInternal: boolean,
): boolean {
  return getActiveDexVersion(featureFlags, isInternal) !== null;
}

const VERSION_LABELS: Record<DexVersion, string> = {
  v2: 'Data Explorer v2',
  v1: 'Data Explorer v1 (legacy)',
};

/**
 * Generates a tooltip message explaining the dex version state.
 *
 * Note: This is only displayed when AppSidebarInternalIcon renders,
 * which only happens when the active feature flag is INTERNAL.
 */
export function getDexInternalTooltip(
  featureFlags: CustomerConfig['featureFlags'],
  tenant: string,
): string {
  const internalView = getActiveDexVersion(featureFlags, true);
  const externalView = getActiveDexVersion(featureFlags, false);

  if (!internalView) {
    return `INTERNAL - not yet available for ${tenant}`;
  }

  const internalLabel = VERSION_LABELS[internalView.version];

  if (externalView === null) {
    return `INTERNAL: You see ${internalLabel}, but ${tenant} has no dex access yet`;
  }

  if (internalView.version !== externalView.version) {
    const externalLabel = VERSION_LABELS[externalView.version];
    return `⚠️ VERSION MISMATCH: You see ${internalLabel}, but ${tenant} sees ${externalLabel}`;
  }

  // Both internal and external see the same version → feature is PUBLIC
  // Note: AppSidebarInternalIcon won't render for PUBLIC flags, so this
  // tooltip won't display. Included for completeness and defensive coding.
  return `${internalLabel} is available for ${tenant}`;
}
